import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, XCircle, CheckCircle, Clock, Gift, Check, Loader2, Package, Shield } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import OrderList from '../../components/OrderList';
import { formatDateTime } from '../../utils/timeUtils';

const REFUND_DESTINATION_LABELS: Record<string, string> = {
  original: '原路退回',
  balance: '退回平台余额',
};

interface TeacherPackage {
  id: string;
  organization: string;
  category: string;
  level_text: string;
  expire_date: string;
  package_source?: string;
  duration_type?: string;
  created_at: string;
}

const durationLabels: Record<string, string> = {
  month: '月包',
  quarter: '季包',
  halfYear: '半年包',
  year: '年包',
  custom: '自定义包',
};

const sourceLabels: Record<string, string> = {
  student_purchase: '自主购买',
  teacher_purchase: '教师购买',
  admin_grant: '管理员发放',
  coupon_redeem: '券码兑换',
};

export default function TeacherOrders() {
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [showRefundDetail, setShowRefundDetail] = useState(false);

  // 券码兑换相关状态
  const [couponCode, setCouponCode] = useState('');
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, type: 'success' as 'success' | 'error', message: '' });

  // 已购刷题包相关状态
  const [packages, setPackages] = useState<TeacherPackage[]>([]);
  const [packagesLoading, setPackagesLoading] = useState(false);

  const openRefundDetail = (order: any) => {
    setSelectedOrder(order);
    setShowRefundDetail(true);
  };

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ show: true, type, message });
  };

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast({ ...toast, show: false }), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  const loadPackages = async () => {
    setPackagesLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const today = (() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      })();

      const { data } = await supabase
        .from('user_packages')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .eq('role', 'teacher')
        .gte('expire_date', today)
        .order('created_at', { ascending: false });

      // 按 (organization, category, level_text, expire_date) 去重
      const seen = new Set<string>();
      const uniquePackages: TeacherPackage[] = [];
      for (const pkg of data || []) {
        const key = `${pkg.organization}|${pkg.category}|${pkg.level_text}|${pkg.expire_date}`;
        if (!seen.has(key)) {
          seen.add(key);
          uniquePackages.push({
            id: pkg.id,
            organization: pkg.organization || '',
            category: pkg.category || '',
            level_text: pkg.level_text || '',
            expire_date: pkg.expire_date || '',
            package_source: pkg.package_source || 'student_purchase',
            duration_type: pkg.duration_type || undefined,
            created_at: pkg.created_at,
          });
        }
      }
      setPackages(uniquePackages);
    } catch {
      // ignore
    } finally {
      setPackagesLoading(false);
    }
  };

  useEffect(() => {
    loadPackages();
  }, []);

  const handleRedeemCoupon = async () => {
    if (!couponCode.trim()) {
      showToast('error', '请输入券码');
      return;
    }
    if (!/^\d{4}[A-Z0-9]{4}$/.test(couponCode.trim())) {
      showToast('error', '券码格式不正确，应为8位字母数字');
      return;
    }
    setRedeemLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        showToast('error', '请先登录');
        setRedeemLoading(false);
        return;
      }
      const session = (await supabase.auth.getSession()).data.session;
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/package-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          action: 'redeemCoupon',
          code: couponCode.trim(),
          userId: user.id,
          role: 'teacher',
        }),
      });
      const result = await response.json();
      if (result.success) {
        showToast('success', `兑换成功！已为您开通${result.data.levelText}刷题包权限`);
        setCouponCode('');
        await loadPackages();
      } else {
        showToast('error', result.error || '券码无效/已使用/已作废，请检查后重试');
      }
    } catch {
      showToast('error', '兑换失败，请稍后重试');
    }
    setRedeemLoading(false);
  };

  const getDaysRemaining = (expireDate: string) => {
    const days = Math.ceil((new Date(expireDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return days;
  };

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">套餐与订单</h1>
      </div>

      {/* 券码兑换区域 */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200 p-5"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center">
            <Gift className="w-5 h-5 text-purple-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800">券码兑换</h3>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
            placeholder="输入8位券码（如：2026D98M）"
            maxLength={8}
            className="flex-1 max-w-xs px-4 py-2.5 rounded-lg border border-purple-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white font-mono tracking-wider"
          />
          <button
            onClick={handleRedeemCoupon}
            disabled={redeemLoading || couponCode.length !== 8}
            className="px-5 py-2.5 bg-gradient-to-r from-purple-500 to-pink-400 text-white rounded-lg font-medium text-sm hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
          >
            {redeemLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            提交兑换
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2 ml-1">
          券码格式：4位年份 + 4位字母数字，兑换后刷题包立即生效
        </p>
      </motion.div>

      {/* 已购刷题包展示区域 */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800">已购刷题包</h3>
            <span className="px-2.5 py-0.5 bg-blue-50 text-blue-600 text-xs font-medium rounded-full">
              {packages.length}
            </span>
          </div>
          <button
            onClick={loadPackages}
            disabled={packagesLoading}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
          >
            {packagesLoading ? '刷新中...' : '刷新'}
          </button>
        </div>

        <div className="p-5">
          {packagesLoading && packages.length === 0 ? (
            <div className="text-center py-8 text-gray-400">加载中...</div>
          ) : packages.length === 0 ? (
            <div className="text-center py-8">
              <Package className="w-12 h-12 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400 text-sm">暂无有效刷题包</p>
              <p className="text-gray-300 text-xs mt-1">可通过券码兑换或联系管理员开通</p>
            </div>
          ) : (
            <div className="space-y-3">
              {packages.map((pkg) => {
                const daysRemaining = getDaysRemaining(pkg.expire_date);
                const isExpiringSoon = daysRemaining <= 7;
                return (
                  <motion.div
                    key={pkg.id}
                    whileHover={{ scale: 1.005 }}
                    className="flex items-center gap-4 p-4 bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-100 hover:shadow-sm transition-all"
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isExpiringSoon ? 'bg-orange-100' : 'bg-blue-100'
                    }`}>
                      <Shield className={`w-5 h-5 ${isExpiringSoon ? 'text-orange-600' : 'text-blue-600'}`} />
                    </div>
                    <div className="flex-1 min-w-0 flex items-center gap-3">
                      <span className="font-semibold text-gray-800 whitespace-nowrap">{pkg.organization}-{pkg.category}-{pkg.level_text}</span>
                      {pkg.duration_type && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full whitespace-nowrap">
                          {durationLabels[pkg.duration_type] || pkg.duration_type}
                        </span>
                      )}
                      {pkg.package_source && (
                        <span className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${
                          pkg.package_source === 'coupon_redeem' ? 'bg-purple-100 text-purple-600' :
                          pkg.package_source === 'admin_grant' ? 'bg-green-100 text-green-600' :
                          'bg-blue-100 text-blue-600'
                        }`}>
                          {sourceLabels[pkg.package_source] || pkg.package_source}
                        </span>
                      )}
                      <span className="text-xs text-gray-400">|</span>
                      <span className={`text-sm whitespace-nowrap ${isExpiringSoon ? 'text-orange-600' : 'text-gray-600'}`}>
                        有效期至 {pkg.expire_date}
                      </span>
                      <span className={`px-3 py-1 rounded-lg text-xs font-medium whitespace-nowrap ${
                        isExpiringSoon
                          ? 'bg-orange-50 text-orange-600 border border-orange-200'
                          : 'bg-green-50 text-green-600 border border-green-200'
                      }`}>
                        {isExpiringSoon ? `${daysRemaining}天后到期` : `剩余${daysRemaining}天`}
                      </span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>

      {/* 订单记录列表 */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="flex items-center gap-3 mb-4">
          <h3 className="text-lg font-semibold text-gray-800">订单记录</h3>
        </div>
        <OrderList
          type="teacher-center"
          mode="page"
          showActions={false}
          onViewRefundDetail={openRefundDetail}
        />
      </motion.div>

      {/* Toast 提示 */}
      <AnimatePresence>
        {toast.show && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed top-4 right-4 z-[60] px-4 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2 ${
              toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
            }`}
          >
            {toast.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* 退款详情弹窗 */}
      <AnimatePresence>
        {showRefundDetail && selectedOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowRefundDetail(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Eye className="w-5 h-5 text-blue-500" />退款详情
                </h3>
                <button onClick={() => setShowRefundDetail(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                  <XCircle className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="p-4 space-y-4">
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">订单号</span>
                    <span className="font-mono">{selectedOrder.orderNo}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">实付金额</span>
                    <span className="text-gray-800">¥{Number(selectedOrder.actualPrice || 0).toFixed(2)}</span>
                  </div>
                </div>
                <div className="border-t border-gray-200 pt-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-800">退款申请已处理</div>
                      <div className="text-xs text-gray-500">{formatDateTime(selectedOrder.refundedAt)}</div>
                    </div>
                  </div>
                  <div className="ml-4 pl-4 border-l-2 border-green-200 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">退款金额</span>
                      <span className="font-bold text-red-600">¥{Number(selectedOrder.refundAmount || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">退款去向</span>
                      <span className="text-gray-800">{REFUND_DESTINATION_LABELS[selectedOrder.refundDestination || 'original'] || '-'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">退款原因</span>
                      <span className="text-gray-800 max-w-[200px] text-right">{selectedOrder.refundReason || '-'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 pt-2">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <Clock className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-800">退款到账中</div>
                      <div className="text-xs text-gray-500">预计1-3个工作日到账</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
